/**
 * Admin · API & Integrations Tab
 * ===============================
 * Centralized view of every external webhook / integration the platform
 * exposes. v2.6.9 batch BH ships with **Newo AI Phone System** — the
 * pattern is reusable so future integrations (Twilio inbound SMS, Webhook
 * for n8n / Zapier, etc.) drop in as additional cards.
 *
 * Newo AI card features:
 *   · Live endpoint URL (auto-derived from REACT_APP_BACKEND_URL) with copy
 *   · Live API key (masked by default, reveal/copy/regenerate)
 *   · Enable/disable toggle (when off, webhook returns 503)
 *   · Live counters (total received + last-received timestamp)
 *   · Recent intakes list (last 50) with trip-id link + payload preview
 *   · curl example tailored to the user's actual key + URL
 */
import { useEffect, useState, useCallback } from "react";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, RotateCw, Phone, CheckCircle2, AlertTriangle, Loader2, RefreshCw, ExternalLink, ClipboardList, Pencil } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const WEBHOOK_PATH = "/api/newo-booking";
// v2.6.9 batch BH-3 · BISON phone system metadata is now editable via admin
// (lives on `app_config.integrations.bison_phone`). These are just fallback
// defaults if the config doc hasn't seeded yet.
const PHONE_SYSTEM_DEFAULTS = {
  active: true,
  data_center: "OKC Works Data Center · OKC",
  phone_number: "(901) 459-9448",
  phone_number_e164: "+19014599448",
};

function copy(text, label = "Copied") {
  if (!navigator.clipboard) return toast.error("Clipboard not available in this browser");
  navigator.clipboard.writeText(text).then(
    () => toast.success(label),
    () => toast.error("Copy failed"),
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Format a one-shot, paste-ready handoff document containing everything the
 * BISON AI integration team needs: endpoint URL, header, method, payload
 * schema, sample curl, BISON-side phone-system metadata, and response shape.
 * v2.6.9 batch BH-2.
 */
function buildHandoffDoc(newo, fullEndpoint, curlExample, phone) {
  const p = phone || PHONE_SYSTEM_DEFAULTS;
  return `═══════════════════════════════════════════════════════════
  BISON AI PHONE SYSTEM · DISPATCH WEBHOOK INTEGRATION
  Buller Taxi Services
═══════════════════════════════════════════════════════════

ENDPOINT URL
  ${fullEndpoint}

HTTP METHOD
  POST

REQUIRED HEADERS
  Content-Type: application/json
  X-API-KEY: ${newo.api_key}

REQUEST BODY · application/json
  {
    "rider_name":          "John Doe",        // required
    "rider_phone":         "+15551234567",    // optional but recommended
    "pickup_address":      "123 Main St, Anytown, USA",   // required
    "destination_address": "456 Oak Ave, Anytown, USA",   // optional
    "pickup_time":         "2026-07-22T14:30:00Z",        // optional · ISO 8601
    "passengers":          1,                              // optional · integer
    "notes":               "Bring receipt",                // optional · free text
    "payment_method":      "cash"                          // optional · "cash" | "card"
  }

SUCCESS RESPONSE · 200 OK
  {
    "status":      "success",
    "trip_id":     "BTS-DISP-XXXXX",
    "received_at": "2026-05-27T19:24:30Z"
  }

ERROR RESPONSES
  401  Invalid or missing X-API-KEY header
  503  Newo AI booking intake is currently disabled by administrator

SAMPLE CURL
${curlExample}

───────────────────────────────────────────────────────────
  BISON-SIDE PHONE SYSTEM
───────────────────────────────────────────────────────────
  Status:               ${p.active ? "ACTIVE" : "INACTIVE"}
  Data Center:          ${p.data_center}
  Active Phone Number:  ${p.phone_number}

═══════════════════════════════════════════════════════════
  Generated ${fmtDate(new Date().toISOString())}
  Bookings received to date: ${newo.total_received || 0}
═══════════════════════════════════════════════════════════`;
}

export default function IntegrationsTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [intakes, setIntakes] = useState([]);
  const [intakesLoading, setIntakesLoading] = useState(false);
  const [revealKey, setRevealKey] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPin, setRegenPin] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");
  // v2.6.9 batch BH-3 · BISON phone metadata editor
  const [phoneEditOpen, setPhoneEditOpen] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState(PHONE_SYSTEM_DEFAULTS);
  const [phoneSaving, setPhoneSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/integrations");
      setCfg(data);
      setLabelDraft(data?.newo_ai?.label || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load integrations");
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadIntakes = useCallback(async () => {
    setIntakesLoading(true);
    try {
      const { data } = await adminApi.get("/admin/integrations/newo/intakes?limit=50");
      setIntakes(data?.items || []);
    } catch {
      // silent — intakes are auxiliary
    } finally {
      setIntakesLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    reloadIntakes();
  }, [reload, reloadIntakes]);

  const newo = cfg?.newo_ai;
  const phone = cfg?.bison_phone || PHONE_SYSTEM_DEFAULTS;
  const fullEndpoint = `${BACKEND}${WEBHOOK_PATH}`;

  const toggleEnabled = async (next) => {
    setSavingEnabled(true);
    try {
      const { data } = await adminApi.put("/admin/integrations/newo", { enabled: next });
      setCfg(data);
      toast.success(next ? "Newo intake enabled" : "Newo intake disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSavingEnabled(false);
    }
  };

  const saveLabel = async () => {
    if (!labelDraft.trim() || labelDraft === newo?.label) return;
    try {
      const { data } = await adminApi.put("/admin/integrations/newo", { label: labelDraft.trim() });
      setCfg(data);
      toast.success("Label updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const openPhoneEdit = () => {
    setPhoneDraft({
      active: phone.active !== false,
      data_center: phone.data_center || "",
      phone_number: phone.phone_number || "",
      phone_number_e164: phone.phone_number_e164 || "",
    });
    setPhoneEditOpen(true);
  };
  const savePhone = async () => {
    setPhoneSaving(true);
    try {
      const { data } = await adminApi.put("/admin/integrations/bison-phone", phoneDraft);
      setCfg(data);
      setPhoneEditOpen(false);
      toast.success("Phone system info updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setPhoneSaving(false);
    }
  };

  const regenerateKey = async () => {
    if (!regenPin.trim()) return toast.error("PIN required");
    setRegenBusy(true);
    try {
      const { data } = await adminApi.post("/admin/integrations/newo/regenerate-key", { pin: regenPin.trim() });
      setCfg(data);
      setRegenPin("");
      setRegenOpen(false);
      setRevealKey(true); // show the new key so they can copy it immediately
      toast.success("API key rotated — copy the new one now and update Newo's settings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN rejected");
    } finally {
      setRegenBusy(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 bg-zinc-900 border-zinc-800" data-testid="integrations-loading">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-500"/>
      </Card>
    );
  }
  if (!newo) {
    return (
      <Card className="p-6 bg-zinc-900 border-zinc-800" data-testid="integrations-empty">
        <p className="text-sm text-zinc-400">No integrations configured yet.</p>
      </Card>
    );
  }

  const maskedKey = newo.api_key
    ? `${newo.api_key.slice(0, 4)}${"•".repeat(Math.max(8, newo.api_key.length - 8))}${newo.api_key.slice(-4)}`
    : "";

  const curlExample = `curl -X POST "${fullEndpoint}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-KEY: ${newo.api_key}" \\
  -d '{"rider_name":"John Doe","rider_phone":"+15551234567","pickup_address":"123 Main St, Anytown, USA","destination_address":"456 Oak Ave, Anytown, USA","pickup_time":"2026-07-22T14:30:00Z"}'`;

  return (
    <div className="space-y-5" data-testid="integrations-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight">API &amp; Integrations</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Webhook URLs and API keys for third-party systems pushing data into Buller Taxi Services.</p>
        </div>
        <Button variant="outline" size="sm" data-testid="integrations-refresh-btn" onClick={() => { reload(); reloadIntakes(); }} className="bg-zinc-950 border-zinc-700">
          <RefreshCw className="h-3.5 w-3.5 mr-1"/>Refresh
        </Button>
      </div>

      {/* ============================ BISON AI CARD ============================ */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="newo-ai-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3 min-w-0">
            <img
              src="/bison-ai-logo.png"
              alt="BISON AI"
              className="h-12 w-auto flex-shrink-0 select-none pointer-events-none"
              style={{ filter: "drop-shadow(0 1px 6px rgba(0,0,0,0.5))" }}
              data-testid="bison-logo"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-lg" data-testid="newo-label">BISON AI Phone System</h3>
                {newo.enabled ? (
                  <Badge className="bg-green-500/20 text-green-300 border-green-400/40 text-[10px]" data-testid="newo-status-badge"><CheckCircle2 className="h-3 w-3 mr-1"/>Webhook Active</Badge>
                ) : (
                  <Badge className="bg-zinc-700/40 text-zinc-400 border-zinc-600 text-[10px]" data-testid="newo-status-badge"><AlertTriangle className="h-3 w-3 mr-1"/>Disabled</Badge>
                )}
              </div>
              {/* v2.6.9 batch BH-2 · BISON-branded phone-system live status pill.
                  Green blinking dot + data-center + active phone number. */}
              <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="phone-system-status">
                <span className="relative inline-flex h-2.5 w-2.5 flex-shrink-0">
                  {phone.active !== false && <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping"/>}
                  <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${phone.active !== false ? "bg-green-400" : "bg-zinc-600"}`}/>
                </span>
                <span className={`text-xs font-bold uppercase tracking-wider ${phone.active !== false ? "text-green-300" : "text-zinc-500"}`}>{phone.active !== false ? "Phone System Active" : "Phone System Offline"}</span>
                <span className="text-[11px] text-zinc-500">·</span>
                <span className="text-[11px] text-zinc-300">{phone.data_center}</span>
                <button type="button" onClick={openPhoneEdit} data-testid="phone-system-edit-btn" className="ml-1 text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-0.5">
                  <Pencil className="h-3 w-3"/>Edit
                </button>
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-[11px] text-zinc-300" data-testid="phone-system-number">
                <Phone className="h-3 w-3 text-amber-300"/>
                <span className="text-zinc-500">Active Phone Number:</span>
                <a href={`tel:${phone.phone_number_e164 || ""}`} className="font-bold text-amber-300 hover:text-amber-200">{phone.phone_number}</a>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed max-w-xl">
                Inbound webhook for the BISON AI phone agent. Each booking arrives as a POST and is dropped directly into the dispatch panel with <code className="text-amber-300">source: newo_ai</code>.
              </p>
            </div>
          </div>
          <Switch
            data-testid="newo-enabled-toggle"
            checked={!!newo.enabled}
            disabled={savingEnabled}
            onCheckedChange={toggleEnabled}
          />
        </div>

        {/* ── Copy All button · v2.6.9 batch BH-2 · gives Newo's integration
            team everything they need in one paste — endpoint URL, header,
            method, payload schema, sample curl, plus the BISON-side phone
            info — formatted as a clean handoff doc. */}
        <Button
          variant="outline"
          size="sm"
          data-testid="newo-copy-all-btn"
          onClick={() => copy(buildHandoffDoc(newo, fullEndpoint, curlExample, phone), "All integration info copied")}
          className="w-full mb-4 bg-amber-400/10 border-amber-400/50 text-amber-300 hover:bg-amber-400/20 font-semibold"
        >
          <ClipboardList className="h-4 w-4 mr-2"/>
          COPY ALL INTEGRATION INFO FOR BISON AI
        </Button>

        {/* ── Endpoint URL row ── */}
        <div className="mb-4">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Endpoint URL</Label>
          <div className="flex gap-2 mt-1">
            <Input
              data-testid="newo-endpoint-input"
              readOnly
              value={fullEndpoint}
              className="bg-zinc-950 border-zinc-700 font-mono text-xs"
            />
            <Button
              size="sm"
              variant="outline"
              data-testid="newo-endpoint-copy-btn"
              onClick={() => copy(fullEndpoint, "Endpoint URL copied")}
              className="bg-zinc-950 border-zinc-700 flex-shrink-0"
            >
              <Copy className="h-3.5 w-3.5 mr-1"/>Copy
            </Button>
          </div>
        </div>

        {/* ── API Key row ── */}
        <div className="mb-4">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">API Key (X-API-KEY header)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              data-testid="newo-apikey-input"
              readOnly
              type="text"
              value={revealKey ? newo.api_key : maskedKey}
              className="bg-zinc-950 border-zinc-700 font-mono text-xs"
            />
            <Button
              size="sm"
              variant="outline"
              data-testid="newo-apikey-toggle-btn"
              onClick={() => setRevealKey((v) => !v)}
              className="bg-zinc-950 border-zinc-700 flex-shrink-0"
              title={revealKey ? "Hide" : "Reveal"}
            >
              {revealKey ? <EyeOff className="h-3.5 w-3.5"/> : <Eye className="h-3.5 w-3.5"/>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="newo-apikey-copy-btn"
              onClick={() => copy(newo.api_key, "API key copied")}
              className="bg-zinc-950 border-zinc-700 flex-shrink-0"
            >
              <Copy className="h-3.5 w-3.5 mr-1"/>Copy
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="newo-apikey-regen-btn"
              onClick={() => setRegenOpen(true)}
              className="bg-rose-500/10 border-rose-400/40 text-rose-300 hover:bg-rose-500/20 flex-shrink-0"
            >
              <RotateCw className="h-3.5 w-3.5 mr-1"/>Rotate
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1">Rotating invalidates the old key immediately — update Newo's configuration before rotating in production.</p>
          {newo.key_rotated_at && (
            <p className="text-[10px] text-amber-300 mt-1">Last rotated {fmtDate(newo.key_rotated_at)}{newo.key_rotated_by ? ` · by ${newo.key_rotated_by}` : ""}</p>
          )}
        </div>

        {/* ── Label editor row ── */}
        <div className="mb-4">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Display Label</Label>
          <div className="flex gap-2 mt-1">
            <Input
              data-testid="newo-label-input"
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              className="bg-zinc-950 border-zinc-700"
            />
            <Button
              size="sm"
              variant="outline"
              data-testid="newo-label-save-btn"
              disabled={labelDraft === newo.label || !labelDraft.trim()}
              onClick={saveLabel}
              className="bg-zinc-950 border-zinc-700 flex-shrink-0"
            >
              Save
            </Button>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="newo-stat-total">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Bookings Received</div>
            <div className="text-2xl font-bold text-indigo-300 meter-display">{newo.total_received || 0}</div>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="newo-stat-last">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Most Recent</div>
            <div className="text-sm font-semibold text-zinc-200 mt-1">{fmtDate(newo.last_received_at)}</div>
          </div>
        </div>

        {/* ── curl example ── */}
        <details className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="newo-curl-example">
          <summary className="text-xs font-bold uppercase tracking-wider text-zinc-300 cursor-pointer">Sample curl request</summary>
          <pre className="text-[11px] font-mono text-zinc-300 mt-2 whitespace-pre-wrap break-all">{curlExample}</pre>
          <Button
            size="sm"
            variant="outline"
            data-testid="newo-curl-copy-btn"
            onClick={() => copy(curlExample, "curl example copied")}
            className="bg-zinc-900 border-zinc-700 mt-2"
          >
            <Copy className="h-3.5 w-3.5 mr-1"/>Copy curl
          </Button>
        </details>
      </Card>

      {/* ============================ RECENT INTAKES ============================ */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="newo-intakes-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-base">Recent Newo Intakes</h3>
          <Button
            variant="outline"
            size="sm"
            data-testid="newo-intakes-refresh-btn"
            onClick={reloadIntakes}
            className="bg-zinc-950 border-zinc-700"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1"/>Refresh
          </Button>
        </div>
        {intakesLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-zinc-500"/>
        ) : intakes.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="newo-intakes-empty">No bookings received yet. Once Newo POSTs to the endpoint above, requests will appear here.</p>
        ) : (
          <div className="space-y-2" data-testid="newo-intakes-list">
            {intakes.map((it) => (
              <div key={it.id} className="bg-zinc-950 border border-zinc-800 rounded p-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-400/40 text-[10px]">{it.trip_id}</Badge>
                    <span className="text-[11px] text-zinc-500">{fmtDate(it.received_at)}</span>
                  </div>
                  <div className="text-sm mt-1.5 font-medium text-zinc-200 truncate">
                    {it.payload?.rider_name || "—"}{it.payload?.rider_phone ? ` · ${it.payload.rider_phone}` : ""}
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-0.5 truncate">
                    {it.payload?.pickup_address || "—"} → {it.payload?.destination_address || "(no destination)"}
                  </div>
                  {it.payload?.pickup_time && (
                    <div className="text-[10px] text-amber-300 mt-0.5">Pickup: {it.payload.pickup_time}</div>
                  )}
                </div>
                <a
                  href={`#trip-${it.trip_id}`}
                  className="text-[11px] text-indigo-300 hover:text-indigo-200 underline self-start sm:self-center inline-flex items-center gap-1"
                  onClick={(e) => { e.preventDefault(); toast.info(`Open Dispatch → search ${it.trip_id}`); }}
                >
                  Dispatch <ExternalLink className="h-3 w-3"/>
                </a>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ============================ REGENERATE DIALOG ============================ */}
      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="newo-regen-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-300">Rotate Newo AI API Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-300">
              Rotating the key invalidates the current one immediately. <strong className="text-rose-300">Newo will stop accepting bookings until you update its settings</strong> with the new key.
            </p>
            <p className="text-xs text-zinc-500">
              Enter your supervisor PIN to confirm:
            </p>
            <Input
              data-testid="newo-regen-pin-input"
              type="password"
              autoComplete="off"
              maxLength={12}
              value={regenPin}
              onChange={(e) => setRegenPin(e.target.value)}
              placeholder="PIN"
              className="bg-zinc-950 border-zinc-700 font-mono text-center text-lg tracking-widest"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="newo-regen-cancel-btn" onClick={() => { setRegenOpen(false); setRegenPin(""); }} className="bg-zinc-950 border-zinc-700">Cancel</Button>
            <Button
              data-testid="newo-regen-confirm-btn"
              onClick={regenerateKey}
              disabled={regenBusy || !regenPin.trim()}
              className="bg-rose-500 hover:bg-rose-400 text-zinc-950 font-bold"
            >
              {regenBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : <><RotateCw className="h-4 w-4 mr-1"/>Rotate Key</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================ PHONE SYSTEM EDIT DIALOG ============================ */}
      <Dialog open={phoneEditOpen} onOpenChange={setPhoneEditOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="phone-system-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-300">Edit BISON Phone System Info</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">
              These fields control the status pill shown on this page. They are operator-facing only and never sent to riders.
            </p>
            <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded p-3">
              <Label className="text-xs">Phone System Active</Label>
              <Switch
                data-testid="phone-edit-active-toggle"
                checked={!!phoneDraft.active}
                onCheckedChange={(v) => setPhoneDraft((p) => ({ ...p, active: v }))}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Data Center Label</Label>
              <Input
                data-testid="phone-edit-datacenter-input"
                value={phoneDraft.data_center || ""}
                onChange={(e) => setPhoneDraft((p) => ({ ...p, data_center: e.target.value }))}
                placeholder="OKC Works Data Center · OKC"
                className="bg-zinc-950 border-zinc-700 mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Display Phone Number</Label>
              <Input
                data-testid="phone-edit-display-input"
                value={phoneDraft.phone_number || ""}
                onChange={(e) => setPhoneDraft((p) => ({ ...p, phone_number: e.target.value }))}
                placeholder="(901) 459-9448"
                className="bg-zinc-950 border-zinc-700 mt-1 meter-display"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">E.164 Phone (for tap-to-call)</Label>
              <Input
                data-testid="phone-edit-e164-input"
                value={phoneDraft.phone_number_e164 || ""}
                onChange={(e) => setPhoneDraft((p) => ({ ...p, phone_number_e164: e.target.value }))}
                placeholder="+19014599448"
                className="bg-zinc-950 border-zinc-700 mt-1 meter-display"
              />
              <p className="text-[10px] text-zinc-500 mt-1">Country code + digits only (no spaces, parens, or dashes).</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="phone-edit-cancel-btn" onClick={() => setPhoneEditOpen(false)} className="bg-zinc-950 border-zinc-700">Cancel</Button>
            <Button
              data-testid="phone-edit-save-btn"
              onClick={savePhone}
              disabled={phoneSaving}
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
            >
              {phoneSaving ? <Loader2 className="h-4 w-4 animate-spin"/> : "Save Phone Info"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
